import {
	Card,
	Tabs,
	Descriptions,
	Tag,
	Button,
	Space,
	Typography,
	Row,
	Col,
	Spin,
	notification,
	Badge,
	Alert,
	List,
	Avatar,
	Table,
	Empty,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	PiUsersDuotone,
	PiArrowLeftDuotone,
	PiPencilDuotone,
	PiCrownDuotone,
	PiCalendarDuotone,
	PiCurrencyDollarDuotone,
	PiUserDuotone,
	PiInfoDuotone,
	PiShoppingCartDuotone,
	PiEyeDuotone,
} from "react-icons/pi";
import { requestGetOrderList, requestGetTeamById } from "../../services/admin.service";
import { formatCurrency } from "../../utils/Formatter";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

function ViewTeamPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [team, setTeam] = useState(null);
	const [orders, setOrders] = useState([]);
	const [activeTab, setActiveTab] = useState("details");

	const orderColumns = [
		{
			title: "Order ID",
			dataIndex: "orderNumber",
			key: "orderNumber",
			render: (text, record) => (
				<div>
					<div style={{ fontWeight: 500 }}>{text}</div>
					<div style={{ fontSize: "12px", color: "#666" }}>ID: {record._id}</div>
				</div>
			),
		},
		{
			title: "Service",
			dataIndex: "serviceName",
			key: "serviceName",
			render: (text, record) => (
				<div>
					{record.items.map((item, index) => (
						<Tag key={index} style={{ marginBottom: 4 }}>
							{item.service.name}
						</Tag>
					))}
				</div>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => {
				const colors = {
					PENDING: "orange",
					PROCESSING: "blue",
					COMPLETED: "green",
					CANCELLED: "red",
					FAILED: "red",
				};
				return <Tag color={colors[status] || "default"}>{status}</Tag>;
			},
		},
		{
			title: "Total",
			dataIndex: "total",
			key: "total",
			render: (amount, record) => formatCurrency(amount, record.currency || "IDR"),
		},
		{
			title: "Created At",
			dataIndex: "createdAt",
			key: "createdAt",
			render: (date) => new Date(date).toLocaleDateString(),
		},
		{
			title: "Actions",
			key: "actions",
			render: (_, record) => (
				<Space size="small">
					<Button
						type="text"
						size="small"
						icon={<PiEyeDuotone />}
						onClick={() => navigate(`/orders/${record._id}`)}
						title="View Order"
					/>
				</Space>
			),
		},
	];

	useEffect(() => {
		document.title = "Cloudiva Console | View Team";
		if (id) {
			fetchTeamDetails();
		}
	}, [id]);

	const fetchTeamDetails = () => {
		setLoading(true);
		requestGetTeamById(id)
			.then((response) => {
				setTeam(response.data.data || response.data);

				fetchTeamOrders();
			})
			.catch((error) => {
				console.error("Error fetching team:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch team details",
				});
				navigate("/teams");
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchTeamOrders = () => {
		requestGetOrderList({
			filter: `team:${id}`,
			limit: 100,
		})
			.then((response) => {
				const orders = response.data.data || response.data;
				setOrders(orders);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch team orders",
				});
			});
	};

	const memberColumns = [
		{
			title: "Member",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<Avatar size="small" style={{ marginRight: 12 }}>
						{text?.charAt(0)?.toUpperCase()}
					</Avatar>
					<div>
						<div style={{ fontWeight: 500 }}>{text}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>{record.email}</div>
					</div>
				</div>
			),
		},
		{
			title: "Phone",
			dataIndex: "phone",
			key: "phone",
			render: (phone) => phone || "N/A",
		},
		{
			title: "Role",
			key: "role",
			render: (_, record) => {
				const isOwner = record._id === team?.ownerId;
				return (
					<Tag
						color={isOwner ? "gold" : "blue"}
						icon={isOwner ? <PiCrownDuotone /> : <PiUserDuotone />}>
						{isOwner ? "Owner" : "Member"}
					</Tag>
				);
			},
		},
	];

	if (loading) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", marginTop: 50 }}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	if (!team) {
		return (
			<div className="page-content">
				<Alert
					message="Team not found"
					description="The team you're looking for doesn't exist."
					type="error"
					showIcon
				/>
			</div>
		);
	}

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/teams")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Team: {team.name}
					</Title>
					<Button
						type="link"
						icon={<PiPencilDuotone />}
						onClick={() => navigate(`/teams/${team._id}/edit`)}>
						Edit Team
					</Button>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Card
					title={
						<Space>
							<PiUsersDuotone size={20} />
							<span>{team.name}</span>
							<Tag color="blue">{team.currency}</Tag>
						</Space>
					}
					extra={
						<Button
							type="primary"
							icon={<PiPencilDuotone />}
							onClick={() => navigate(`/teams/${team._id}/edit`)}>
							Edit Team
						</Button>
					}>
					<Tabs activeKey={activeTab} onChange={setActiveTab}>
						<TabPane
							tab={
								<Space>
									<PiInfoDuotone />
									<span>Team Details</span>
								</Space>
							}
							key="details">
							<Row gutter={24}>
								<Col span={12}>
									<Card
										title={
											<Space>
												<PiUsersDuotone />
												<span>Basic Information</span>
											</Space>
										}
										size="small">
										<Descriptions column={1} size="small">
											<Descriptions.Item label="Team Name">
												<Text strong>{team.name}</Text>
											</Descriptions.Item>
											<Descriptions.Item label="Description">
												<Text>{team.description || "No description provided"}</Text>
											</Descriptions.Item>
											<Descriptions.Item label="Currency">
												<Tag color="blue" icon={<PiCurrencyDollarDuotone />}>
													{team.currency}
												</Tag>
											</Descriptions.Item>
											<Descriptions.Item label="Total Members">
												<Badge count={team.members?.length || 0} color="blue" />
											</Descriptions.Item>
											<Descriptions.Item label="Created At">
												<Space>
													<PiCalendarDuotone />
													<span>{new Date(team.createdAt).toLocaleString()}</span>
												</Space>
											</Descriptions.Item>
											{team.updatedAt && (
												<Descriptions.Item label="Last Updated">
													<Space>
														<PiCalendarDuotone />
														<span>{new Date(team.updatedAt).toLocaleString()}</span>
													</Space>
												</Descriptions.Item>
											)}
										</Descriptions>
									</Card>
								</Col>
								<Col span={12}>
									<Card
										title={
											<Space>
												<PiCrownDuotone />
												<span>Team Owner</span>
											</Space>
										}
										size="small">
										{team.owner ? (
											<Descriptions column={1} size="small">
												<Descriptions.Item label="Name">
													<Text strong>{team.owner.name}</Text>
												</Descriptions.Item>
												<Descriptions.Item label="Email">
													<Text code>{team.owner.email}</Text>
												</Descriptions.Item>
												<Descriptions.Item label="Phone">
													<Text>{team.owner.phone || "N/A"}</Text>
												</Descriptions.Item>
											</Descriptions>
										) : (
											<Text type="secondary">Owner information not available</Text>
										)}
									</Card>
								</Col>
							</Row>
						</TabPane>

						<TabPane
							tab={
								<Space>
									<PiUsersDuotone />
									<span>Members ({team.members?.length || 0})</span>
								</Space>
							}
							key="members">
							<Card
								title={
									<Space>
										<PiUsersDuotone />
										<span>Team Members</span>
									</Space>
								}
								size="small">
								{team.members && team.members.length > 0 ? (
									<Table
										columns={memberColumns}
										dataSource={team.members}
										pagination={false}
										rowKey="_id"
										size="small"
									/>
								) : (
									<Alert
										message="No Members"
										description="This team currently has no members."
										type="info"
										showIcon
									/>
								)}
							</Card>
						</TabPane>

						<TabPane
							tab={
								<Space>
									<PiShoppingCartDuotone />
									<span>Orders ({orders.length})</span>
								</Space>
							}
							key="orders">
							<Card
								title={
									<Space>
										<PiShoppingCartDuotone />
										<span>Team Orders</span>
									</Space>
								}
								size="small">
								{orders && orders.length > 0 ? (
									<Table
										columns={orderColumns}
										dataSource={orders}
										// loading={ordersLoading}
										rowKey="_id"
										pagination={{
											pageSize: 10,
											showSizeChanger: true,
											showQuickJumper: true,
											showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
										}}
										size="small"
									/>
								) : (
									<Empty description="No orders found for this customer" />
								)}
							</Card>
						</TabPane>

						<TabPane
							tab={
								<Space>
									<PiCalendarDuotone />
									<span>Activity</span>
								</Space>
							}
							key="activity">
							<Card
								title={
									<Space>
										<PiCalendarDuotone />
										<span>Recent Activity</span>
									</Space>
								}
								size="small">
								<List
									dataSource={[
										{
											title: "Team created",
											description: `Team "${team.name}" was created`,
											datetime: team.createdAt,
										},
										...(team.updatedAt && team.updatedAt !== team.createdAt
											? [
													{
														title: "Team updated",
														description: `Team "${team.name}" was last updated`,
														datetime: team.updatedAt,
													},
											  ]
											: []),
									]}
									renderItem={(item) => (
										<List.Item>
											<List.Item.Meta
												avatar={<Avatar icon={<PiCalendarDuotone />} />}
												title={item.title}
												description={
													<div>
														<div>{item.description}</div>
														<Text type="secondary" style={{ fontSize: "12px" }}>
															{new Date(item.datetime).toLocaleString()}
														</Text>
													</div>
												}
											/>
										</List.Item>
									)}
								/>
							</Card>
						</TabPane>
					</Tabs>
				</Card>
			</div>
		</div>
	);
}

export default ViewTeamPage;
